import { BrandLoader } from './BrandLoader'
import { AlertOctagon, Inbox, Lock, SearchX, WifiOff } from '@/design-system/icons'
import type { ReactNode } from 'react'
import { ApiError } from '@/api/client'
import { useT } from '@/i18n'
import { Illustration, type IllustrationKind } from './illustrations'
import { LOTTIE, LottieScene } from './LottieScene'
import { Button, cn, Icon, Skeleton, type IconComponent } from './primitives'

/** The scene that fits an icon when the caller didn't pick one explicitly. */
const sceneFor = (icon: IconComponent): IllustrationKind =>
  icon === SearchX ? 'search' : icon === AlertOctagon ? 'error' : icon === WifiOff ? 'offline' : icon === Lock ? 'locked' : 'empty'

export function EmptyState({ icon = Inbox, illustration, media, title, description, action, className, compact }: { icon?: IconComponent; illustration?: IllustrationKind | null; /** Custom artwork (e.g. a Lottie scene) shown instead of the built-in illustration. */ media?: ReactNode; title: ReactNode; description?: ReactNode; action?: ReactNode; className?: string; compact?: boolean }) {
  const scene = illustration === null ? null : (illustration ?? sceneFor(icon))
  return (
    <div className={cn('animate-scale-in flex flex-col items-center justify-center text-center', compact ? 'gap-2 px-4 py-8' : 'gap-3 px-6 py-14', className)}>
      {media && !compact ? (
        media
      ) : scene && !compact ? (
        <Illustration kind={scene} size={132} />
      ) : (
        <div className="flex size-11 items-center justify-center rounded-[12px] bg-brand-soft text-brand-text">
          <Icon icon={icon} size={20} />
        </div>
      )}
      <div className="max-w-sm">
        <p className="text-base font-bold text-fg-strong">{title}</p>
        {description && <p className="mt-1 text-sm text-fg-muted">{description}</p>}
      </div>
      {action}
    </div>
  )
}

export function FilteredEmptyState({ onClear }: { onClear?: () => void }) {
  const { t } = useT()
  return (
    <EmptyState
      icon={SearchX}
      title={t('empty.filtered')}
      description={t('empty.filteredHint')}
      action={onClear && <Button size="sm" onClick={onClear}>{t('common.clear')}</Button>}
    />
  )
}

/** Renders the right message for any error thrown by the API layer. */
export function ErrorState({ error, onRetry, className, compact }: { error: unknown; onRetry?: () => void; className?: string; compact?: boolean }) {
  const { t } = useT()
  const e = error instanceof ApiError ? error : null
  if (e?.status === 403) {
    const required = (e.details as { required?: string } | undefined)?.required
    return <EmptyState compact={compact} className={className} icon={Lock} title={t('error.forbidden')} description={required ? t('error.forbiddenHint', { permission: String(required) }) : e.message} />
  }
  const offline = e?.code === 'OFFLINE'
  const network = e?.code === 'NETWORK_ERROR' || e?.code === 'UPSTREAM_UNAVAILABLE' || e?.code === 'UPSTREAM_TIMEOUT'
  return (
    <EmptyState
      compact={compact}
      className={className}
      icon={offline || network ? WifiOff : AlertOctagon}
      media={offline || network ? <LottieScene src={LOTTIE.noConnection} size={190} className="-my-4" /> : undefined}
      title={offline ? t('error.offline') : network ? t('error.network') : t('error.title')}
      description={
        <>
          {offline ? t('error.offlineHint') : e?.message ?? (error instanceof Error ? error.message : String(error))}
          {e?.requestId && <span className="mt-1 block font-mono text-2xs text-fg-subtle">{t('error.requestId', { id: e.requestId })}</span>}
        </>
      }
      action={onRetry && <Button size="sm" onClick={onRetry}>{t('common.retry')}</Button>}
    />
  )
}

export function TableSkeleton({ rows = 8, columns = 6 }: { rows?: number; columns?: number }) {
  return (
    <div className="divide-y divide-border" aria-busy>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex items-center gap-4 px-4 py-3">
          {Array.from({ length: columns }, (_, c) => (
            <Skeleton key={c} className="h-3.5" style={{ width: `${c === 0 ? 14 : 8 + ((r * 7 + c * 13) % 14)}%` }} />
          ))}
        </div>
      ))}
    </div>
  )
}

/** A page still loading: the portal's saucer loader (owner, 2026-10-23: «فين انيميشن مركبة الفضاء ال ف بورتال»). */
export function PageSkeleton() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center p-4 sm:p-6" aria-busy>
      <BrandLoader size={34} pad="12vh 0" />
    </div>
  )
}
